import React from 'react';
import type { CalendarMonthView } from '../../../types';

interface MonthViewProps {
  data: CalendarMonthView;
  onDayClick?: (date: string) => void;
  currency?: string;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  onGoToToday?: () => void;
}

export const MonthView: React.FC<MonthViewProps> = ({ data, onDayClick, currency = 'USD', onNavigatePrevious, onNavigateNext, onGoToToday }) => {
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const weekdaysShort = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  };

  const isToday = (dateStr: string) => {
    const today = new Date().toISOString().split('T')[0];
    const dayDate = new Date(dateStr).toISOString().split('T')[0];
    return today === dayDate;
  };

  const getDayClassName = (
    isCurrentMonth: boolean,
    isWorkingDay: boolean,
    hasTimeOff: boolean,
    bookingsCount: number,
    date: string
  ) => {
    const baseClass = 'min-h-[120px] p-3 border-r border-b border-gray-200 transition-colors';

    if (!isCurrentMonth) {
      return `${baseClass} bg-gray-50 text-gray-400`;
    }

    if (hasTimeOff) {
      return `${baseClass} bg-yellow-50 cursor-pointer hover:bg-yellow-100`;
    }

    if (!isWorkingDay) {
      return `${baseClass} bg-gray-50`;
    }

    if (isToday(date)) {
      return `${baseClass} bg-purple-50 cursor-pointer hover:bg-purple-100 ring-2 ring-purple-500 ring-inset`;
    }

    if (bookingsCount > 0) {
      return `${baseClass} bg-white cursor-pointer hover:bg-purple-50`;
    }

    return `${baseClass} bg-white cursor-pointer hover:bg-green-50`;
  };

  const getMobileDayClassName = (
    isCurrentMonth: boolean,
    isWorkingDay: boolean,
    hasTimeOff: boolean,
    bookingsCount: number,
    date: string
  ) => {
    const baseClass = 'aspect-square flex flex-col items-center justify-center rounded-lg transition-colors';

    if (!isCurrentMonth) {
      return `${baseClass} text-gray-300`;
    }

    if (hasTimeOff) {
      return `${baseClass} bg-yellow-50 cursor-pointer active:bg-yellow-100`;
    }

    if (!isWorkingDay) {
      return `${baseClass} text-gray-400`;
    }

    if (isToday(date)) {
      return `${baseClass} bg-purple-100 cursor-pointer active:bg-purple-200 ring-2 ring-purple-500`;
    }

    if (bookingsCount > 0) {
      return `${baseClass} cursor-pointer active:bg-purple-50`;
    }

    return `${baseClass} cursor-pointer active:bg-gray-50`;
  };

  return (
    <>
      {/* Mobile Layout */}
      <div className="md:hidden">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekdaysShort.map((day) => (
            <div
              key={day}
              className="text-center text-xs font-medium text-gray-500 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        {data.weeks.map((week) => (
          <div key={week.week_number} className="grid grid-cols-7 gap-1 mb-1">
            {week.days.map((day, index) => (
              <div
                key={index}
                className={getMobileDayClassName(
                  day.is_current_month,
                  day.is_working_day,
                  day.has_time_off,
                  day.bookings_count,
                  day.date
                )}
                onClick={() => day.is_current_month && onDayClick?.(day.date)}
              >
                <span
                  className={`text-sm font-semibold ${
                    isToday(day.date) ? 'text-purple-600' : ''
                  }`}
                >
                  {day.day_number}
                </span>
                {day.is_current_month && day.bookings_count > 0 && (
                  <div className="flex items-center gap-0.5 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                    <span className="text-[10px] font-medium text-purple-600">{day.bookings_count}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {onNavigatePrevious && (
                <button
                  onClick={onNavigatePrevious}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
                  aria-label="Previous month"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                {data.month_name} {data.year}
              </h3>
              {onNavigateNext && (
                <button
                  onClick={onNavigateNext}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
                  aria-label="Next month"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
              {onGoToToday && (
                <button
                  onClick={onGoToToday}
                  className="ml-1 px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Today
                </button>
              )}
            </div>
            <div className="flex gap-6 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {data.summary.total_bookings}
                </div>
                <div className="text-gray-500">Bookings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(data.summary.total_revenue_cents)}
                </div>
                <div className="text-gray-500">Revenue</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {data.summary.working_days}
                </div>
                <div className="text-gray-500">Working Days</div>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar grid */}
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Weekday headers */}
            <div className="grid grid-cols-7 bg-gray-100">
              {weekdays.map((day) => (
                <div
                  key={day}
                  className="p-3 text-center text-sm font-semibold text-gray-700 border-r border-b border-gray-200"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Weeks */}
            {data.weeks.map((week) => (
              <div key={week.week_number} className="grid grid-cols-7">
                {week.days.map((day, index) => (
                  <div
                    key={index}
                    className={getDayClassName(
                      day.is_current_month,
                      day.is_working_day,
                      day.has_time_off,
                      day.bookings_count,
                      day.date
                    )}
                    onClick={() => day.is_current_month && onDayClick?.(day.date)}
                  >
                    {/* Day number */}
                    <div className="flex items-start justify-between mb-2">
                      <span
                        className={`text-sm font-semibold ${
                          day.is_current_month ? 'text-gray-900' : 'text-gray-400'
                        } ${isToday(day.date) ? 'text-purple-600' : ''}`}
                      >
                        {day.day_number}
                      </span>
                      {day.has_time_off && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-800">
                          Off
                        </span>
                      )}
                    </div>

                    {/* Booking count and revenue */}
                    {day.is_current_month && day.bookings_count > 0 && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                          <span className="text-xs font-medium text-gray-700">
                            {day.bookings_count} {day.bookings_count === 1 ? 'booking' : 'bookings'}
                          </span>
                        </div>
                        {day.revenue_cents > 0 && (
                          <div className="text-xs font-semibold text-green-600">
                            {formatCurrency(day.revenue_cents)}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Non-working day indicator */}
                    {day.is_current_month && !day.is_working_day && !day.has_time_off && (
                      <div className="text-xs text-gray-400">Not working</div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <div className="flex items-center gap-6 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-purple-50 border-2 border-purple-500"></div>
              <span>Today</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-purple-100"></div>
              <span>Has bookings</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-50 border border-yellow-200"></div>
              <span>Time off</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-50 border border-gray-200"></div>
              <span>Not working</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
